<template>
  <component :is="tag" class="fts-tippy">
    <div ref="trigger" :class="triggerClass">
      <slot name="trigger" />
    </div>

    <div ref="content" class="tippy-content" :class="contentClass">
      <slot name="content" v-bind="tippyContent" />
    </div>
  </component>
</template>

<script>
import tippy, {followCursor, roundArrow} from "tippy.js";
import { clientStore } from '../store/clientStore';
import 'tippy.js/dist/svg-arrow.css';
import 'tippy.js/themes/light.css';
import 'tippy.js/themes/light-border.css';

export default {
  props: {
    tag: {
      type: String,
      default: 'div'
    },
    triggerClass: String,
    contentClass: String,
    content: [String, Number],
    options: Object,
    request: Object,
    /* popper options */
    popperPreventOverflow: Boolean,
  },
  data: () => ({
    tippyContent: null,
    defaultProps: {
      appendTo: () => document.body,
      allowHTML: true,
      arrow: true,
      delay: [500, 0], /* ms [show, hide] */
      duration: 0, /* ms [show, hide] */
      followCursor: false,
      interactive: false,
      maxWidth: 350,
      placement: "top",
      theme: "",
      trigger: "mouseenter focus"
    }
  }),
  methods: {
    async init() {
      let trigger = this.$refs.trigger;
      let options = this.defaultProps;
      /* options */
      if (this.options) {
        for (const key of Object.keys(this.options)) {
          if (options.hasOwnProperty(key)) {
            options[key] = this.options[key];
          }
        }
        /* arrow */
        if (options.arrow)  
          options.arrow = roundArrow;
      }  
      /* request */
      if (this.request) {
        options.onShow = async tippy => {
          try {
            const res = await clientStore.dispatch(
              this.request.dispatchName, 
              this.request.payload
            );
            this.tippyContent = res.data
          } catch(err) {  
            console.error(err);
          } finally {
            tippy.popperInstance.update();
          }
        }
      }
      /* content */
      if (this.$refs.content.childElementCount) {
        options.content = this.$refs.content;
      } else if (this.content) {
        options.content = this.content;
        options.onShow = tippy => {
          if (tippy.props.content !== this.content) 
            tippy.setContent(this.content);
        }
      }
      /* popper options */
      let popperModifiers = [];
      if (this.popperPreventOverflow) {
        popperModifiers.push({
          name: 'preventOverflow',
          options: {
            altAxis: true,
            tether: false,
          },
        });
      }

      if (popperModifiers.length) {
        options.popperOptions = {
          modifiers: popperModifiers,
        }
      }
      /* on trigger */
      options.onTrigger = (instance, event) => {
        this.$emit('onTrigger', { instance: instance, event: event });
      };
      /* create tippy */
      this.tippyInstance = tippy(trigger, {
        ...options,
        plugins: [followCursor],
      });
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    if (this.tippyInstance && this.tippyInstance.state) {
      if (this.tippyInstance.state.isMounted) 
        this.tippyInstance.unmount();
      else if (this.tippyInstance.state.isEnabled) 
        this.tippyInstance.disable();
    }
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.tippy-box {
  padding: .375rem .75rem;
  font-size: .75rem;
  background-color: #333;
  text-align: inherit;
  color: #fff;
  border-radius: .25rem;
  .tippy-arrow {
    fill: #333;
  }
  // tomato
  &[data-theme~='tomato'] {
    background-color: tomato;
    color: yellow;
    &[data-placement^='top'] > .tippy-svg-arrow {
      fill: tomato;
    }
    &[data-placement^='bottom'] > .tippy-svg-arrow {
      fill: tomato;
    }
    &[data-placement^='left'] > .tippy-svg-arrow {
      fill: tomato;
    }
    &[data-placement^='right'] > .tippy-svg-arrow {
      fill: tomato;
    }
  }
  // light
  &[data-theme~='light-border'] {
    box-shadow: 0 3px 14px -0.5px rgba(0, 8, 16, 0.1);
    background-color: var(--fts-popover-background);
    color: var(--fts-text);
    >.tippy-svg-arrow {
      fill: var(--fts-popover-background);
    }
  }
}
</style>